/* import __COLOCATED_TEMPLATE__ from './reset-email-form.hbs'; */
import { task } from 'ember-concurrency';
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import Component from '@ember/component';
import { confirmEmailValidator } from 'sh-www/utils/validators';
import { handleFormErrorOrModal } from 'sh-www/utils/handles-errors';
import { showModal } from 'sh-www/utils/modal-controls';

/** @type {string[]} */
const FORM_INPUTS = [
  'new_email',
  'new_email_confirm',
  'password',
];

/** @type {string[][]} */
const FORM_TUPLES = [
  ['new_email', 'emailValidator'],
  ['new_email_confirm', 'confirmEmailValidator'],
  ['password', 'passwordValidator'],
];

/** @module */
const SettingsResetEmailForm = Component.extend({
  /** @inheritDoc */
  tagName: '',

  /** @type {string} */
  currentEmail: '',

  /** @type {function} */
  resetEmail: undefined,

  /** @type {string} */
  new_email: '',

  /** @type {string} */
  new_email_confirm: '',

  /** @type {string} */
  password: '',

  /** @type {Boolean} */
  saving: readOnly('submitResetEmailForm.isRunning'),

  /** @type {Ember.ComputedProperty} */
  emailValidator: computed('currentEmail', 'new_email', function() {
    const newEmail = this.new_email;

    if (newEmail.length === 0) return 'Email must be entered';

    // prevent repeat emails
    if (newEmail === this.currentEmail) {
      return 'You cannot use the same email';
    }
  }),

  /** @type {Ember.ComputedProperty} */
  confirmEmailValidator: computed('new_email', 'new_email_confirm', function() {
    return confirmEmailValidator(this.new_email, this.new_email_confirm);
  }),

  /** @type {Ember.ComputedProperty} */
  passwordValidator: computed('password', function() {
    if (this.password.length === 0) return 'Password must be entered';
  }),

  init() {
    this._super(...arguments);
    this.set('errors', {});
  },

  /**
   * Called when the user submits the reset email form.  Handles validation and makes request to server.
   */
  submitResetEmailForm: task(function *() {
    const params = {
      'new_email' : this.new_email,
      'password' : this.password
    };

    try {
      yield this.resetEmail(params);
      const data = {
        isShowCancelButton: false,
        titleText: 'New Email Submitted',
        bodyText: `Thanks, but you aren't done yet! Please verify your new email address by clicking on the link we just sent to ${params.new_email}`,
        actionButtonText: 'OK'
      };
      showModal(this, 'confirmation', data);
      this._clearForm();
    } catch (error) {
      // handle incorrect password
      if (error.errors?.['generic-exception']?.[0] === 'Invalid credentials.') {
        this.set(`errors.password`, 'Incorrect password');
        return;
      }

      // handle all other errors
      handleFormErrorOrModal(
        this,
        error,
        FORM_INPUTS,
        false,
        'There was an error updating your email. Please refresh and try again.',
        'settingsProfileFail -- error updating user email.' + JSON.stringify(error)
      );
    }
  }),

  /** @inheritDoc */
  actions: {
    /**
     * @param {string} field
     * @param {string} value
     */
    updateField(field, value) {
      this.setProperties({ [field]: value, [`errors.${field}`]: undefined });
    },

    /**
     * @param {string} field
     * @param {string} validator
     */
    validateField(field, validator) {
      const error = this[validator];

      if (error) {
        this.set(`errors.${field}`, error);
      }
    },

    /**
     *
     */
    handleSubmit() {
      if (!this.saving && this._validateForm()) {
        this.submitResetEmailForm.perform();
      }
    },
  },

  /**
   * @returns {boolean}
   * @private
   */
  _validateForm() {
    const [errors, hasError] = FORM_TUPLES.reduce(([errors, hasError], [key, validator]) => {
      const err = this[validator];

      if (err) {
        errors[key] = err;
        hasError = true;
      }

      return [errors, hasError];
    }, [{ }, false]);

    this.set('errors', errors);
    return !hasError;
  },

  /**
   * Clears the email reset form.
   *
   * @private
   */
   _clearForm: function() {
    this.set('new_email', '');
    this.set('new_email_confirm', '');
    this.set('password', '');
  }
});

export default SettingsResetEmailForm;
